<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  companyId: { type: [Number, String], default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
  labelList: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'toggle',
  'updateContact',
  'showContact',
  'select',
  'avatarHover',
  'updateLabels',
]);

const { t } = useI18n();
const labels = useMapGetter('labels/getLabels');

const contactsFormRef = ref(null);
const showLabelDropdown = ref(false);
const labelQuery = ref('');
const selectedLabels = ref([...props.labelList]);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  companyId: props.companyId,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const filteredLabels = computed(() => {
  if (!labelQuery.value) return labels.value;
  return labels.value.filter(label =>
    label.title.toLowerCase().includes(labelQuery.value.toLowerCase())
  );
});

const isLabelSelected = labelTitle => {
  return selectedLabels.value.includes(labelTitle);
};

const labelColors = computed(() => {
  const labelMap = {};
  labels.value.forEach(label => {
    labelMap[label.title] = label.color;
  });
  return labelMap;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);

const toggleSelect = checked => {
  emit('select', checked);
};

const handleAvatarHover = isHovered => {
  emit('avatarHover', isHovered);
};

const toggleLabelDropdown = () => {
  showLabelDropdown.value = !showLabelDropdown.value;
  labelQuery.value = '';
  selectedLabels.value = [...props.labelList];
};

const closeLabelDropdown = () => {
  showLabelDropdown.value = false;
};

const handleLabelToggle = labelTitle => {
  const index = selectedLabels.value.indexOf(labelTitle);
  if (index > -1) {
    selectedLabels.value.splice(index, 1);
  } else {
    selectedLabels.value.push(labelTitle);
  }
};

const handleUpdateLabels = () => {
  emit('updateLabels', { id: props.id, labels: selectedLabels.value });
  closeLabelDropdown();
};
</script>

<template>
  <div class="relative">
    <CardLayout
      :key="id"
      layout="row"
      :class="{
        'outline-n-weak !bg-n-slate-3 dark:!bg-n-solid-3': isSelected,
      }"
    >
      <div class="flex items-center justify-start flex-1 gap-4">
        <div
          class="relative"
          @mouseenter="handleAvatarHover(true)"
          @mouseleave="handleAvatarHover(false)"
        >
          <Avatar
            :name="name"
            :src="thumbnail"
            :size="42"
            :status="availabilityStatus"
            hide-offline-status
          >
            <template v-if="selectable" #overlay="{ size }">
              <label
                class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px] border border-n-weak"
                :style="{ width: `${size}px`, height: `${size}px` }"
                @click.stop
              >
                <Checkbox
                  :model-value="isSelected"
                  @change="event => toggleSelect(event.target.checked)"
                />
              </label>
            </template>
          </Avatar>
        </div>
        <div class="flex flex-col gap-0.5 flex-1">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span class="text-base font-medium truncate text-n-slate-12">
              {{ name }}
            </span>
            <span class="inline-flex items-center gap-1">
              <span
                v-if="additionalAttributes?.companyName"
                class="i-ph-building-light size-4 text-n-slate-10 mb-0.5"
              />
              <span
                v-if="additionalAttributes?.companyName"
                class="text-sm truncate text-n-slate-11"
              >
                {{ additionalAttributes.companyName }}
              </span>
            </span>
          </div>
          <div
            class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1"
          >
            <div v-if="email" class="truncate max-w-72" :title="email">
              <span class="text-sm text-n-slate-11">
                {{ email }}
              </span>
            </div>
            <div v-if="email" class="w-px h-3 truncate bg-n-slate-6" />
            <span v-if="phoneNumber" class="text-sm truncate text-n-slate-11">
              {{ phoneNumber }}
            </span>
            <div v-if="phoneNumber" class="w-px h-3 truncate bg-n-slate-6" />
            <span
              v-if="countryDetails"
              class="inline-flex items-center gap-2 text-sm truncate text-n-slate-11"
            >
              <Flag :country="countryDetails.countryCode" class="size-3.5" />
              {{ formattedLocation }}
            </span>
            <div v-if="countryDetails" class="w-px h-3 truncate bg-n-slate-6" />
            <Button
              :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
              variant="link"
              size="xs"
              @click="onClickViewDetails"
            />
          </div>
          <div
            v-if="labelList && labelList.length > 0"
            class="flex flex-wrap items-center gap-2 mt-2"
          >
            <span
              v-for="label in labelList"
              :key="label"
              class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full text-white"
              :style="{ backgroundColor: labelColors[label] || '#64748b' }"
            >
              {{ label }}
            </span>
            <Button
              icon="i-lucide-plus"
              variant="ghost"
              size="xs"
              class="size-6 p-0"
              @click="toggleLabelDropdown"
            />
          </div>
          <div v-else class="mt-2">
            <Button
              icon="i-lucide-plus"
              variant="ghost"
              size="xs"
              :label="t('CONTACTS_LAYOUT.CARD.ADD_LABEL')"
              @click="toggleLabelDropdown"
            />
          </div>
        </div>
      </div>

      <!-- Label Dropdown -->
      <div
        v-if="showLabelDropdown"
        v-on-click-outside="closeLabelDropdown"
        class="absolute right-0 top-12 z-20 w-64 bg-n-alpha-3 backdrop-blur-[100px] border-n-weak rounded-lg border border-solid shadow-md"
        role="dialog"
      >
        <div
          class="flex items-center justify-between p-2.5 border-b border-n-weak"
        >
          <span class="text-sm font-medium">{{
            t('CONTACTS_LAYOUT.CARD.MANAGE_LABELS')
          }}</span>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            size="xs"
            @click="closeLabelDropdown"
          />
        </div>
        <div class="flex flex-col max-h-60 min-h-0">
          <header class="py-2 px-2.5">
            <Input
              v-model="labelQuery"
              type="search"
              :placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
              icon-left="i-lucide-search"
              size="sm"
              class="w-full"
            />
          </header>
          <ul
            v-if="labels.length > 0"
            class="flex-1 overflow-y-auto m-0 list-none"
            role="listbox"
          >
            <li
              v-for="label in filteredLabels"
              :key="label.id"
              class="my-1 mx-0 py-0 px-2.5"
              role="option"
            >
              <label
                class="items-center rounded-md cursor-pointer flex py-1 px-2.5 hover:bg-n-slate-3 dark:hover:bg-n-solid-3"
              >
                <input
                  :checked="isLabelSelected(label.title)"
                  type="checkbox"
                  class="my-0 ltr:mr-2.5 rtl:ml-2.5"
                  @change="() => handleLabelToggle(label.title)"
                />
                <span
                  class="overflow-hidden flex-grow w-full text-sm whitespace-nowrap text-ellipsis"
                >
                  {{ label.title }}
                </span>
                <span
                  class="rounded-md h-3 w-3 flex-shrink-0 border border-solid border-n-weak"
                  :style="{ backgroundColor: label.color }"
                />
              </label>
            </li>
          </ul>
          <div v-else class="p-2 text-center">
            <span class="text-sm text-n-slate-11">{{
              t('CONTACTS_BULK_ACTIONS.NO_LABELS_FOUND')
            }}</span>
          </div>
          <footer class="p-2 border-t border-n-weak">
            <Button
              sm
              type="submit"
              class="w-full"
              :label="t('BULK_ACTION.LABELS.ASSIGN_SELECTED_LABELS')"
              :disabled="
                selectedLabels.length === props.labelList.length &&
                selectedLabels.every(l => props.labelList.includes(l))
              "
              @click="handleUpdateLabels"
            />
          </footer>
        </div>
      </div>

      <Button
        icon="i-lucide-chevron-down"
        variant="ghost"
        color="slate"
        size="xs"
        :class="{ 'rotate-180': isExpanded }"
        @click="onClickExpand"
      />

      <template #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <div class="flex flex-col gap-6 p-6 border-t border-n-strong">
              <ContactsForm
                ref="contactsFormRef"
                :contact-data="contactData"
                @update="handleFormUpdate"
              />
              <div>
                <Button
                  :label="
                    t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                  "
                  size="sm"
                  :is-loading="isUpdating"
                  :disabled="isUpdating || isFormInvalid"
                  @click="handleUpdateContact"
                />
              </div>
            </div>
            <ContactDeleteSection
              :selected-contact="{
                id: props.id,
                name: props.name,
              }"
            />
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
